/** The bytes that the check reads from the start of the picked file. */
const SNIFF_BYTES = 16;

const ascii = (bytes: Uint8Array, start: number, end: number): string =>
  String.fromCharCode(...Array.from(bytes.subarray(start, end)));

/** `ftyp` brands of a picture: an image in an MP4 box is not a song. */
const IMAGE_BRANDS = new Set(['heic', 'heix', 'hevc', 'hevx', 'heim', 'heis', 'mif1', 'msf1', 'avif', 'avis']);

/**
 * True when the first bytes are a master type: WAV, FLAC, AIFF, MP3, AAC or MP4 audio. The same
 * rule as music24-api `isMasterAudio` (`Catalog/song/masterAudioSniff.ts`), which checks the
 * stored file again. A text file named `.mp3` is none of them (QA 2026-10-28, brk-11).
 */
export function looksLikeAudio(bytes: Uint8Array): boolean {
  if (bytes.length < 4) {
    return false;
  }
  const head = ascii(bytes, 0, 4);
  if (head === 'fLaC' || head.startsWith('ID3')) {
    return true;
  }
  // WAV: RIFF, its big-endian twin RIFX, and the 64-bit forms RF64 and BW64 that some studio
  // tools write.
  if (bytes.length >= 12 && (head === 'RIFF' || head === 'RIFX' || head === 'RF64' || head === 'BW64')) {
    return ascii(bytes, 8, 12) === 'WAVE';
  }
  if (bytes.length >= 12 && head === 'FORM') {
    const form = ascii(bytes, 8, 12);
    return form === 'AIFF' || form === 'AIFC';
  }
  if (bytes.length >= 12 && ascii(bytes, 4, 8) === 'ftyp') {
    return !IMAGE_BRANDS.has(ascii(bytes, 8, 12).trim().toLowerCase());
  }
  const first = bytes[0] ?? 0;
  const second = bytes[1] ?? 0;
  return first === 0xff && second >= 0xe0;
}

/**
 * Reads the start of a picked file. A file that the browser cannot read answers true: the upload
 * then says why, and the server checks the bytes again.
 */
export async function isAudioFile(file: Blob): Promise<boolean> {
  try {
    const buffer = await file.slice(0, SNIFF_BYTES).arrayBuffer();
    return looksLikeAudio(new Uint8Array(buffer));
  } catch (error) {
    console.warn('Could not read the start of the audio file', error);
    return true;
  }
}
